import React from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';

import { Text } from '@atlaskit/primitives/compiled';

import type { UpdateWorkItemAllFieldTypesAction } from '../../../../../common/types';
import { getActionData } from '../../../utils';

import type { singleSelectUserPickerField_updateWorkItemAllFieldTypesActionRenderer$key } from './__generated__/singleSelectUserPickerField_updateWorkItemAllFieldTypesActionRenderer.graphql';
import { SingleSelectUserPickerFieldSkeleton } from './SingleSelectUserPickerFieldSkeleton';
import { useHydratedUser } from './useHydratedUser';

const messages = defineMessages({
	assign: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.single-select-user-picker-field.assign',
		defaultMessage: 'Assign to {userName}',
		description: 'Label for the change action in the single select user picker field',
	},
	assignGeneric: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.single-select-user-picker-field.assign-generic',
		defaultMessage: 'Assign {userName} to {fieldName}',
		description: 'Label for the change action in the single select user picker field',
	},
	unassign: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.single-select-user-picker-field.unassign',
		defaultMessage: 'Unassign user',
		description: 'Label for the change action in the single select user picker field',
	},
	unassignGeneric: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.single-select-user-picker-field.unassign-generic',
		defaultMessage: 'Unassign user from {fieldName}',
		description: 'Label for the change action in the single select user picker field',
	},
	assignWithOriginalUser: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.single-select-user-picker-field.assign-with-original-user',
		defaultMessage: 'Assigning from {originalUser} to {userName}',
		description: 'Label for the change action in the single select user picker field',
	},
});

export type Props = {
	queryRef: singleSelectUserPickerField_updateWorkItemAllFieldTypesActionRenderer$key;
	action: UpdateWorkItemAllFieldTypesAction;
	onInvalidAction?: () => void;
};

export const SingleSelectUserPickerField = ({
	queryRef,
	action,
	onInvalidAction,
}: Props): React.JSX.Element | null => {
	di(useHydratedUser);

	const { formatMessage } = useIntl();

	const { fieldKey, fieldValue } = getActionData(action);

	const data = useFragment(
		graphql`
			fragment singleSelectUserPickerField_updateWorkItemAllFieldTypesActionRenderer on JiraSingleSelectUserPickerField {
				fieldId
				name
				user {
					accountId
					name
				}
			}
		`,
		queryRef,
	);

	const suggestedUserAccountId =
		typeof fieldValue === 'string' ? fieldValue.trim() : fieldValue?.toString() || null;

	const isFieldRenderable = Boolean(data?.fieldId && data.fieldId === fieldKey);

	const needsHydration = Boolean(
		isFieldRenderable && suggestedUserAccountId && suggestedUserAccountId !== data?.user?.accountId,
	);

	const hydratedData = useHydratedUser(suggestedUserAccountId, needsHydration, onInvalidAction);

	const { user, isLoading, error } =
		needsHydration && hydratedData
			? { user: hydratedData.user, isLoading: hydratedData.isLoading, error: hydratedData.error }
			: { user: data?.user, isLoading: false, error: null };

	const isAssignable = Boolean((user || isLoading) && suggestedUserAccountId);
	const isNotFound = Boolean(!user && !isLoading && suggestedUserAccountId);

	const assignMessage =
		data?.user?.accountId && data?.user?.accountId !== user?.accountId
			? formatMessage(messages.assignWithOriginalUser, {
					originalUser: data?.user?.name,
					userName: user?.name,
				})
			: formatMessage(data?.fieldId === 'assignee' ? messages.assign : messages.assignGeneric, {
					userName: user?.name,
					...(data?.fieldId !== 'assignee' && { fieldName: data?.name }),
				});

	const unassignMessage = formatMessage(
		data?.fieldId === 'assignee' ? messages.unassign : messages.unassignGeneric,
		{
			...(data?.fieldId !== 'assignee' && { fieldName: data?.name }),
		},
	);

	const formattedMessage = isAssignable ? assignMessage : unassignMessage;

	if (isLoading) {
		return <SingleSelectUserPickerFieldSkeleton />;
	}

	if (!isFieldRenderable || isNotFound || error) {
		return null;
	}

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-all-fields-action-renderer.ui.action-card.card-fields-list.single-select-user-picker-field"
		>
			{formattedMessage}
		</Text>
	);
};
